import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://mxfjellqpjftpaxyuwve.supabase.co';
const SUPABASE_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im14ZmplbGxxcGpmdHBheHl1d3ZlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4OTYwNTQsImV4cCI6MjA5MjQ3MjA1NH0.08vKzxr6DFA4f7PLS6goL5duTM58eCSNFhTngfLUIMk';

// Monkey-patch navigator.locks to prevent DevSpaces proxy lock errors
if (typeof navigator !== 'undefined' && navigator.locks) {
  const origRequest = navigator.locks.request.bind(navigator.locks);
  navigator.locks.request = async (name, opts, cb) => {
    try { return await origRequest(name, opts, cb); }
    catch { if (cb) return cb({ name, mode: 'exclusive' }); if (typeof opts === 'function') return opts({ name, mode: 'exclusive' }); }
  };
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: {
    storageKey: 'tidjarai-auth',
    flowType: 'implicit',
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Separate client for DB operations — no auth overhead
export const db = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: { persistSession: false, autoRefreshToken: false },
});

// Auth helpers
export async function signInWithEmail(email) {
  try {
    // Timeout after 10s to prevent hanging
    const result = await Promise.race([
      supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin }
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
    ]);
    const error = result?.error;
    if (!error) {
      await db.from('leads').upsert({ email, email_verified: false }, { onConflict: 'email' });
    }
    return { success: !error, error: error?.message };
  } catch (e) {
    // If timeout or lock error, try direct API call
    console.warn('[Auth] OTP failed, trying direct:', e.message);
    try {
      const resp = await fetch(`https://mxfjellqpjftpaxyuwve.supabase.co/auth/v1/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
        body: JSON.stringify({ email, gotrue_meta_security: {}, data: {} })
      });
      if (resp.ok) {
        await db.from('leads').upsert({ email, email_verified: false }, { onConflict: 'email' });
        return { success: true };
      }
      const err = await resp.json();
      return { success: false, error: err.msg || err.error_description || 'Erreur' };
    } catch (e2) {
      return { success: false, error: e2.message };
    }
  }
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function getUser() {
  const { data } = await supabase.auth.getUser();
  return data.user;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export function onAuthChange(callback) {
  return supabase.auth.onAuthStateChange(async (_event, session) => {
    if ((_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED') && session?.user?.email) {
      localStorage.setItem('tidjarai_user_email', session.user.email);
      // Mark lead as verified
      const { error } = await db.from('leads').upsert({
        email: session.user.email,
        email_verified: true,
        verified_at: new Date().toISOString(),
        fingerprint: localStorage.getItem('tf_fp') || null,
      }, { onConflict: 'email' });
      if (error) console.warn('[Auth] Lead upsert error:', error);

      // Migrate localStorage analyses to Supabase
      try {
        const history = JSON.parse(localStorage.getItem('tidjarai_history') || localStorage.getItem('tradeflow_history') || '[]');
        if (history.length > 0 && session.user.id) {
          for (const h of history.slice(0, 20)) {
            await db.from('user_analyses').upsert({
              user_id: session.user.id,
              email: session.user.email,
              type: 'import',
              description: h.description,
              hs_code: h.code_sh,
              country: h.pays,
              activity_code: h.codeActivite,
              result_data: h.result || null,
            }, { onConflict: 'id', ignoreDuplicates: true });
          }
          console.log('[Auth] Migrated', history.length, 'analyses to Supabase');
        }
      } catch {}
    }
    callback(session);
  });
}

// Analytics tracking — pass email explicitly
export async function trackAnalysis(type, description, hsCode, country, userEmail) {
  try {
    const monthYear = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
    
    try {
      await db.from('anonymous_sessions').upsert({
        fingerprint: localStorage.getItem('tf_fp') || 'unknown',
        simulation_count: parseInt(localStorage.getItem(`tidjarai_analyses_${localStorage.getItem('tf_fp')||'x'}_${monthYear}`) || '0'),
        month_year: monthYear, updated_at: new Date().toISOString(),
      }, { onConflict: 'fingerprint,month_year' });
    } catch {}

    try {
      await db.from('analysis_log').insert({
        type, description, hs_code: hsCode, country,
        user_email: userEmail || null,
        fingerprint: localStorage.getItem('tf_fp') || 'unknown',
      });
    } catch (e) { console.warn('[Track] insert error:', e); }
  } catch (e) { console.warn('[Track]', e); }
}

// Admin settings
export async function getAdminSettings() {
  const { data } = await supabase.from('admin_settings').select('*').single();
  return data || { require_email: true, free_limit: 3 };
}

export async function updateAdminSettings(settings) {
  return supabase.from('admin_settings').upsert({ id: 1, ...settings }, { onConflict: 'id' });
}
